import React, {
  useEffect,
  useState,
  Fragment,
  useMemo,
  useCallback,
  useRef,
} from 'react'
import injectStyles from 'react-jss'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'

import BookCallIcon from '../../Assets/Images/icons/bookCall'
import { authorization } from '../../Module/Auth/Auth'
import { apiClient } from '../../Module/API/Client'
import { getHomePageData, getSubmittedCampaigns } from './gql'
import LoadingIndicator from '../../Components/LoadingIndicator/LoadingIndicator'
import { styles } from './styles'

import { fetchClientCampaignsList } from '../Campaigns/gql'
import {
  addAuthParamsToCampaignBuilderUrl,
  getCampaignBuilderPageUrl,
} from '../Country/Page/Pipeline/PipelineView/utils'
import { createSSCampaign } from '../../Components/ClientDashboard/CampaignView/gql'
import { gtagWrapper } from '../../Utils/Functions'
import { openLinkInSameTab } from '../Campaigns/Page/InternalFiles/Container/utils'
import {
  getRecentCampaigns,
  getRecentCampaignsCards,
  getDeclinedCampaignCount,
  getHasSubmittedCampaigns,
} from './utils'
import OutlinedButton from './components/OutlinedButton'
import CreateCampaignIcon from '../../Assets/Images/icons/createCampaign'
import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'
import {
  CAMPAIGN_ENUM_TYPES,
  CAMPAIGN_SUBTYPES_ENUM,
  EDDM_DOCUMENT_FORMATS,
  ADM_DOCUMENTS_FORMATS,
  DocumentFormats,
  CAMPAIGN_ENUM_TYPES_TO_TITLE,
  CAMPAIGN_ENUM_TYPES_TO_CAMPAIGN_NAME,
  EDDM_SIGNUP,
} from '../../shared/constants'
import { CONFIG } from '../../Config/config'
import Paused from '../../Assets/Images/icons/paused.js'
import Banners from './components/Banners'
import classNames from 'classnames'
import { useFeatureFlags } from '../../Context/FeatureFlagsContext'
import { useWrappedPosthog } from '../../Components/PostHogProvider/hooks.js'
import useSmallScreen from '../Auth/SignUp/components/useSmallScreen.js'
import BookingModal from './components/BookingModal/BookingModal'
import CampaignCarousel from './components/CampaignCarousel'

const Home = ({ classes }) => {
  const requestIdRef = useRef(0)
  const posthog = useWrappedPosthog()
  const { isAdmEnabled } = useFeatureFlags()
  const user = authorization.getUser()
  const userCountryId = user.client.countryId
  const clientId = user.client.id
  const isEddmSignup = user.client.createdBy === EDDM_SIGNUP

  const allowedOrigins = [CONFIG.NEXT_BASE_URL]
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [recentCampaigns, setRecentCampaigns] = useState([])
  const [countryCode, setCountryCode] = useState('US')
  const [countrySettings, setCountrySettings] = useState({})
  const [newCampaignCTAOptions, setNewCampaignCTAOptions] = useState([])
  const [declinedCampaignCount, setDeclinedCampaignCount] = useState(0)
  const [isNewCampaignCTAOpened, setIsNewCampaignCTAOpened] = useState(false)
  const [hasSubmittedCampaigns, setHasSubmittedCampaigns] = useState([])
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const history = useHistory()
  const smallScreen = useSmallScreen()
  const currentClient = useSelector(state => state.clients.clientView)
  const canNavigateCampaignBuilder =
    !authorization.isClient() ||
    (currentClient && currentClient.isSelfServeEnabled)

  const handleNewCampaignClick = () => {
    if (posthog) {
      posthog.capture('Create New Campaign', {
        'event_category': 'Homepage',
      })
    }
    setIsNewCampaignCTAOpened(!isNewCampaignCTAOpened)
  }

  const handleCreateCampaign = async ({ type, subtype }, stripeCouponCode) => {
    try {
      setIsCreating(true)
      const clientCampaignsListResponse = await fetchClientCampaignsList({
        clientIds: [clientId],
      })

      const flyerType =
        (countryCode === 'US' || countryCode === 'CA') &&
        type === CAMPAIGN_ENUM_TYPES.LETTERBOX
          ? DocumentFormats.DOOR_HANGER
          : type === CAMPAIGN_ENUM_TYPES.DIRECTMAIL
          ? subtype === CAMPAIGN_SUBTYPES_ENUM.EDDM
            ? EDDM_DOCUMENT_FORMATS.BEST_SELLER
            : ADM_DOCUMENTS_FORMATS.POSTCARD_9x6
          : DocumentFormats.A6

      const flyerWeight = type === CAMPAIGN_ENUM_TYPES.DIRECTMAIL ? null : '250'
      const baPerLocation =
        type === CAMPAIGN_ENUM_TYPES.HANDTOHAND
          ? countrySettings.baPerLocation
          : null

      let campaignCount = clientCampaignsListResponse
        ? clientCampaignsListResponse.length + 1
        : 1

      const campaignNumber =
        campaignCount < 10
          ? `00${campaignCount}`
          : campaignCount >= 10 && campaignCount < 100
          ? `0${campaignCount}`
          : `${campaignCount}`

      const name = `${
        CAMPAIGN_ENUM_TYPES_TO_CAMPAIGN_NAME[subtype ? subtype : type]
      } Campaign - ${campaignNumber}`

      if (posthog) {
        posthog.capture('New Campaign Channel Selected', {
          'event_category': 'Homepage',
          'campaign_name': name,
          'channel': type,
        })
      }

      const response = await createSSCampaign({
        name,
        type,
        subtype,
        flyerType,
        flyerWeight,
        baPerLocation,
        lastActiveStep: 'home',
        stripeCouponCode,
      })
      const { data } = await response.json()
      const { campaignId } = data.createSSCampaign
      const url = getCampaignBuilderPageUrl({
        campaignId,
        clientId,
        lastActiveStep: 'home',
        channel: type,
        subtype,
      })
      const linkWithAuth = addAuthParamsToCampaignBuilderUrl(url, clientId)
      gtagWrapper({
        event: 'ss_campaign_created',
        client_id: clientId.toString(),
        user_id: user.id.toString(),
        campaign_id: campaignId.toString(),
      })
      openLinkInSameTab(linkWithAuth)
    } catch (err) {
      setIsCreating(false)
      throw new Error(err)
    }
  }

  const getData = useCallback(
    async countryId => {
      const currentRequestId = ++requestIdRef.current

      const req = await getHomePageData({ countryId, clientId })
      const {
        data: {
          country: {
            code,
            countryDefaultSettings: {
              enabledChannelAdm,
              enabledChannelEddm,
              enabledChannelLetterbox,
              enabledChannelPreciseLocations,
              ...restCountrySettings
            },
          },
          campaignsBuilderData,
        },
      } = await req.json()

      const campaigns = isEddmSignup
        ? campaignsBuilderData &&
          campaignsBuilderData.nodes.filter(
            campaign => campaign.subtype === CAMPAIGN_SUBTYPES_ENUM.EDDM,
          )
        : campaignsBuilderData &&
          campaignsBuilderData.nodes.filter(campaign => !campaign.workflowId)

      if (currentRequestId !== requestIdRef.current) {
        return
      }
      const newCampaignCTAOptions = []

      if (enabledChannelAdm && isAdmEnabled) {
        newCampaignCTAOptions.push({
          type: CAMPAIGN_ENUM_TYPES.DIRECTMAIL,
          subtype: CAMPAIGN_SUBTYPES_ENUM.ADM,
        })
      }
      if (enabledChannelEddm) {
        newCampaignCTAOptions.push({
          type: CAMPAIGN_ENUM_TYPES.DIRECTMAIL,
          subtype: CAMPAIGN_SUBTYPES_ENUM.EDDM,
        })
      }
      if (enabledChannelLetterbox) {
        newCampaignCTAOptions.push({
          type: CAMPAIGN_ENUM_TYPES.LETTERBOX,
          subtype: null,
        })
      }
      if (enabledChannelPreciseLocations) {
        newCampaignCTAOptions.push({
          type: CAMPAIGN_ENUM_TYPES.HANDTOHAND,
          subtype: null,
        })
      }

      const campaignCTAOptions =
        isEddmSignup && enabledChannelEddm
          ? [
              {
                type: CAMPAIGN_ENUM_TYPES.DIRECTMAIL,
                subtype: CAMPAIGN_SUBTYPES_ENUM.EDDM,
              },
            ]
          : newCampaignCTAOptions

      setNewCampaignCTAOptions(campaignCTAOptions)
      setRecentCampaigns(getRecentCampaigns(campaigns))
      setDeclinedCampaignCount(getDeclinedCampaignCount(campaigns))

      const submittedCampaigns = await getSubmittedCampaigns({
        clientId,
      })
      const { data } = await submittedCampaigns.json()

      const submittedCampaignsData = data.campaignsBuilderData
        ? data.campaignsBuilderData.nodes.filter(
            campaign => !campaign.workflowId,
          )
        : []

      setHasSubmittedCampaigns(getHasSubmittedCampaigns(submittedCampaignsData))

      setCountrySettings(restCountrySettings)
      setCountryCode(code)
      setIsLoading(false)
    },
    [isAdmEnabled, clientId, userCountryId],
  )

  const getDataWithoutUserCountryId = async id => {
    const req = await apiClient.getUserById(id)
    const {
      client: { countryId },
    } = await req.json()
    return await getData(countryId)
  }

  useEffect(() => {
    if (userCountryId) {
      getData(userCountryId)
    } else {
      getDataWithoutUserCountryId(user.id)
    }
  }, [userCountryId, isAdmEnabled, getData])

  useEffect(() => {
    const handleMessageOld = event => {
      if (allowedOrigins.includes(event.origin) && event.data === 'message') {
        const token = JSON.stringify(localStorage.apiKey)
        event.source.postMessage(token, event.origin)
      }
    }
    window.addEventListener('message', handleMessageOld)

    return () => {
      window.removeEventListener('message', handleMessageOld)
    }
  }, [])

  const recentCampaignsCards = useMemo(
    () =>
      canNavigateCampaignBuilder
        ? getRecentCampaignsCards({
            recentCampaigns,
            clientId,
            classes,
            history,
            smallScreen,
          })
        : [],
    [
      recentCampaigns,
      canNavigateCampaignBuilder,
      clientId,
      classes,
      history,
      smallScreen,
    ],
  )

  const noChannelsEnabled =
    newCampaignCTAOptions.every(channel => !channel.type) ||
    !canNavigateCampaignBuilder
  return (
    <div className={classes.root}>
      {isLoading || isCreating ? (
        <div className={classes.loadingWrapper}>
          <LoadingIndicator label={'Loading'} />
        </div>
      ) : (
        <div className={classes.content}>
          {canNavigateCampaignBuilder && (
            <Banners
              hasSubmittedCampaigns={hasSubmittedCampaigns}
              countryCode={countryCode}
              handleCreateCampaign={handleCreateCampaign}
            />
          )}
          <div className={classes.title} cypress_id={'welcomeTitle'}>
            Welcome, {user.firstName}!
          </div>
          {!recentCampaigns.length ? null : (
            <div className={classes.subtitle}>Recent campaigns</div>
          )}
          {!recentCampaigns.length ? null : (
            // (
            //   <div className={classes.text}>
            //     You don’t have any campaigns yet.
            //   </div>
            // )
            <Fragment>
              {declinedCampaignCount ? (
                <div className={classes.text}>
                  You have {declinedCampaignCount} campaign
                  {declinedCampaignCount > 1 ? 's' : ''} that need your
                  attention. Please, review reasons, edit and resubmit your
                  campaign{declinedCampaignCount > 1 ? 's' : ''}.
                </div>
              ) : null}
            </Fragment>
          )}

          {!recentCampaigns.length ? null : (
            <div
              className={classNames(
                classes.campaignBlock,
                'Home-campaignBlock',
              )}
            >
              <CampaignCarousel campaigns={recentCampaignsCards} />
            </div>
          )}
          <div className={classes.subtitle}>Campaign creation</div>
          <div
            className={classNames(classes.campaignBlock, 'Home-campaignBlock')}
          >
            <div
              className={classNames(classes.guideCard)}
              cypress_id={'createCampaignCard'}
            >
              <div className={classes.guideLeftBlock}>
                <div className={classes.guideTextBlock}>
                  <p
                    className={classes.guideTitle}
                    cypress_id={'createCampCardTitle'}
                  >
                    {noChannelsEnabled
                      ? 'No channels available'
                      : 'Create a campaign'}
                  </p>

                  <p
                    className={classes.cardText}
                    cypress_id={'createCampCardText'}
                  >
                    {noChannelsEnabled
                      ? 'Sorry, there are currently no channels available to create a campaign in your country.'
                      : 'Choose a distribution channel and create a campaign instantly!'}
                  </p>
                </div>
                <div>
                  {!noChannelsEnabled && (
                    <Fragment>
                      <OutlinedButton
                        alwaysActive={true}
                        icon={
                          isNewCampaignCTAOpened ? <RemoveIcon /> : <AddIcon />
                        }
                        disabled={false}
                        onClick={handleNewCampaignClick}
                        label="New Campaign"
                        cypress_id={'newCampBtn'}
                      />
                      {isNewCampaignCTAOpened && (
                        <div
                          className={classes.newCampaignCTAOptionsWrapper}
                          cypress_id={'newCampaignCTAOptionsWrapper'}
                        >
                          {newCampaignCTAOptions.map(option => (
                            <p
                              onClick={() => handleCreateCampaign(option)}
                              cypress_id={'newCampaignChannelOption'}
                            >
                              {
                                CAMPAIGN_ENUM_TYPES_TO_TITLE[
                                  option.subtype ? option.subtype : option.type
                                ]
                              }
                            </p>
                          ))}
                        </div>
                      )}
                    </Fragment>
                  )}
                </div>
              </div>
              {noChannelsEnabled ? (
                <Paused />
              ) : (
                <div className={classes.svgCampaignIcon}>
                  <CreateCampaignIcon />
                </div>
              )}
            </div>
            <div
              className={classNames(classes.guideCard)}
              cypress_id={'haveQuestionsCard'}
            >
              <div className={classes.guideLeftBlock}>
                <div className={classes.guideTextBlock}>
                  <p
                    className={classes.guideTitle}
                    cypress_id={'haveQuestionsCardTitle'}
                  >
                    Have any questions?
                  </p>
                  <p
                    className={classes.cardText}
                    cypress_id={'haveQuestionsCardText'}
                  >
                    Book a call with our customer success team
                  </p>
                </div>
                {smallScreen && (
                  <div className={classes.guideIcon}>
                    <BookCallIcon />
                  </div>
                )}
                <OutlinedButton
                  onClick={() => {
                    if (posthog) {
                      posthog.capture('Book a Call', {
                        'event_category': 'Homepage',
                      })
                    }
                    setIsBookingModalOpen(true)
                  }}
                  label="Book a call"
                  cypress_id={'bookACallBtn'}
                  className={classes.guideButton}
                />
              </div>
              {!smallScreen && (
                <div className={classes.guideIcon}>
                  <BookCallIcon />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        submitter={{
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
        }}
        country={{
          code: countryCode,
          name: countrySettings && countrySettings.name,
        }}
        companyName={user.client && user.client.name}
        userId={user.id}
      />
    </div>
  )
}

export default injectStyles(styles)(Home)
